import { useCallback, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { getDb } from '@/offline/db'
import { retryLostMedia, uploadMedia } from '@/offline/media'
import { httpTransport } from '@/offline/transport'
import { useNavigate } from 'react-router-dom'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import {
  blockedByError,
  putBackCommand,
  retryCommand,
  setAsideCommand,
  setAsideTargets,
  useOutbox,
  useSyncStatus,
  type OutboxRecord,
} from '@/offline'
import { useSupervisorPin } from '@/features/gate/components/SupervisorPin'
import { useGateRoster } from '@/features/gate/data/hooks'
import { cropName } from '@/features/gate/v1/text'
import { downloadExport } from '@/offline/exportData'
import type { OpenedOutboxRecord } from '@/offline'
import { RecordDetail } from './sync/RecordDetail'
import { useRecordPhotos } from './sync/photos'
import { photoIdsOf, summarise, type SummaryLookups } from './sync/summary'
import {
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  Strip,
  V1Header,
  V1Screen,
  WaitCta,
} from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'

/** A key's words, else the fallback (a record type or state the app has no words for yet). */
function word(t: (k: MessageKey) => string, key: string, fallback: string): string {
  const w = t(key as MessageKey)
  return w === key ? fallback : w
}

type Filter = 'all' | 'pending' | 'held' | 'errors' | 'photos' | 'sent' | 'setAside'
const FILTER_STATUSES: Partial<Record<Filter, string[]>> = {
  pending: ['PENDING', 'SENDING', 'WAIT_FOR_PARENT'],
  held: ['HELD'],
  errors: ['ERROR'],
  sent: ['APPLIED', 'DUPLICATE'],
  setAside: ['SET_ASIDE'],
}

const TONE: Record<string, { fg: string; bg: string }> = {
  APPLIED: { fg: C.ok, bg: C.okWash },
  DUPLICATE: { fg: C.ok, bg: C.okWash },
  HELD: { fg: C.accDark, bg: C.warnWash },
  ERROR: { fg: C.bad, bg: C.badWash },
  SET_ASIDE: { fg: C.muted, bg: C.sunk },
}

/**
 * Why a record is stuck, in the phone's words: the server's code first (SESSION_CODE_TAKEN …),
 * then the phone's own (PARENT_NEVER_ARRIVED, PAYLOAD_LOCKED …), else the raw message.
 */
function why(t: (k: MessageKey) => string, rec: OutboxRecord): string | null {
  for (const code of [rec.result?.error?.code, rec.last_error_code]) {
    if (!code) continue
    const key = `sync.why.${code}`
    const w = t(key as MessageKey)
    if (w !== key) return w
  }
  return rec.last_error
}

/** Everything waiting on this phone and what the server said, in the alignment map's look. */
export function SyncPage() {
  const { t } = useI18n()
  const fmt = useFormat()
  const navigate = useNavigate()
  const s = useSyncStatus()
  const outbox = useOutbox(200)
  const blocked = useMemo(() => blockedByError(outbox), [outbox])
  const pin = useSupervisorPin()
  const [notice, setNotice] = useState<string | null>(null)

  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<OpenedOutboxRecord | null>(null)
  const [exporting, setExporting] = useState(false)
  const roster = useGateRoster()
  const look = useMemo<SummaryLookups>(() => {
    const byId = new Map((roster?.farmers ?? []).map((f) => [f.user_id, f.name]))
    return {
      farmerName: (id) => byId.get(id) ?? null,
      cropName: (id) => cropName(roster?.config?.crops ?? [], id),
    }
  }, [roster])
  const words = useCallback(
    (key: string, vars?: Record<string, string | number>) => t(key as MessageKey, vars),
    [t],
  )

  const stats: [Filter, number, string, string][] = [
    ['pending', s.pending, t('sync.statPending'), C.ink],
    ['held', s.held, t('sync.statHeld'), C.accDark],
    ['errors', s.errors, t('sync.statErrors'), C.bad],
    ['photos', s.mediaPending + s.mediaLost, t('sync.statPhotos'), C.ink],
    ['sent', s.total - s.pending - s.held - s.errors - s.setAside, t('sync.statSent'), C.ok],
    ...(s.setAside > 0
      ? [
          ['setAside', s.setAside, t('sync.statSetAside'), C.muted] as [
            Filter,
            number,
            string,
            string,
          ],
        ]
      : []),
  ]
  const shown = outbox.filter((r) => FILTER_STATUSES[filter]?.includes(r.status) ?? true)

  const exportAll = async () => {
    setExporting(true)
    try {
      const r = await downloadExport()
      setNotice(t('sync.exported', { records: r.records, photos: r.photos }))
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e))
    } finally {
      setExporting(false)
    }
  }

  /** A supervisor confirms with his PIN; the record and what depends on it leave the queue. */
  const setAside = async (rec: OutboxRecord) => {
    const what = word(t, `cmd.${rec.type}`, rec.type.replace(/_/g, ' '))
    const children = setAsideTargets(outbox, rec.id).length - 1
    const sup = await pin.request(
      <>
        {t('sync.setAsideAsk', { what })}
        {children > 0 && <> {t('sync.setAsideChildren', { count: children })}</>}
      </>,
    )
    if (!sup) return
    const ids = await setAsideCommand(rec.id, {
      userId: sup.user_id,
      name: sup.name,
      reason:
        `${rec.result?.error?.code ?? rec.last_error_code ?? 'ERROR'}: ${rec.last_error ?? ''}`.slice(
          0,
          300,
        ),
    })
    setNotice(t('sync.setAsideDone', { count: ids.length }))
    void s.syncNow()
  }

  return (
    <V1Screen
      nav
      header={<V1Header pending={s.pending} updatable icon="cloudup" title={t('sync.title')} />}
      footer={
        <Footer>
          {s.phase === 'syncing' ? (
            <WaitCta>{t('sync.syncing')}</WaitCta>
          ) : (
            <Cta label={t('sync.now')} icon="cloudup" onClick={() => void s.syncNow()} />
          )}
        </Footer>
      }
    >
      {s.phase === 'auth_required' && (
        <Strip icon="user" onClick={() => navigate('/login?next=/m/sync')}>
          {t('sync.signInNeeded')}
        </Strip>
      )}
      {s.lastError && s.phase === 'error' && <Strip icon="bang">{s.lastError}</Strip>}
      {notice && <Strip icon="ok">{notice}</Strip>}

      {s.mediaLost > 0 && (
        <Strip icon="bang">{t('sync.p.lostStrip', { count: s.mediaLost })}</Strip>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(stats.length, 5)}, minmax(0, 1fr))`,
          gap: 6,
        }}
      >
        {stats.map(([key, value, label, color]) => (
          <button
            type="button"
            key={key}
            aria-pressed={filter === key}
            onClick={() => setFilter(filter === key ? 'all' : key)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '4px 2px',
              borderRadius: 10,
              background: filter === key ? C.warnWash : C.surface,
              border: `1.5px solid ${filter === key ? C.accDark : C.rule}`,
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            <b style={{ fontSize: 22, lineHeight: 1.15, color }}>{value}</b>
            <span style={{ fontSize: 13, color: C.muted }}>{label}</span>
          </button>
        ))}
      </div>
      <span style={{ fontSize: 16, color: C.muted }}>
        {s.lastSyncedAt
          ? t('sync.lastSynced', { when: fmt.relative(s.lastSyncedAt) })
          : t('sync.never')}
      </span>

      {filter !== 'all' && (
        <Btn icon="undo" onClick={() => setFilter('all')}>
          {t('sync.showAll')}
        </Btn>
      )}
      {filter === 'photos' ? (
        <WaitingPhotos />
      ) : outbox.length === 0 ? (
        <Strip icon="ticks">{t('sync.queueEmpty')}</Strip>
      ) : shown.length === 0 ? (
        <Strip icon="ticks">{t('sync.nothingHere')}</Strip>
      ) : (
        shown.map((rec) => (
          <Record
            key={rec.id}
            rec={rec}
            line={summarise(rec.type, rec.payload, look, words).line}
            blocked={blocked.has(rec.id)}
            onOpen={() => setOpen(rec)}
            onSetAside={() => void setAside(rec)}
          />
        ))
      )}
      {open && (
        <RecordDetail
          rec={outbox.find((r) => r.id === open.id) ?? open}
          title={word(t, `cmd.${open.type}`, open.type.replace(/_/g, ' '))}
          statusWord={word(
            t,
            `outbox.${(outbox.find((r) => r.id === open.id) ?? open).status}`,
            open.status,
          )}
          reason={why(t, outbox.find((r) => r.id === open.id) ?? open)}
          summary={summarise(open.type, open.payload, look, words)}
          onSetAside={() => void setAside(open)}
          onClose={() => setOpen(null)}
        />
      )}
      {exporting ? (
        <WaitCta>{t('sync.exporting')}</WaitCta>
      ) : (
        <Btn icon="book" onClick={() => void exportAll()}>
          {t('sync.exportAll')}
        </Btn>
      )}
      {pin.sheet}
    </V1Screen>
  )
}

function Record({
  rec,
  line,
  blocked,
  onOpen,
  onSetAside,
}: {
  rec: OpenedOutboxRecord
  line: string | null
  blocked: boolean
  onOpen: () => void
  onSetAside: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const reason = why(t, rec)
  const tone = TONE[rec.status] ?? { fg: C.muted, bg: C.sunk }
  const held = rec.result?.exception_ids?.length ?? 0
  const photoIds = useMemo(() => photoIdsOf(rec, rec.payload), [rec])
  const photos = useRecordPhotos(photoIds)
  const sent = photos.filter((p) => p.state === 'SENT').length
  const lost = photos.filter((p) => p.state === 'LOST').length
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer' }}
    >
      <ItemRow
        padding="8px 10px"
        lead={
          <span
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 10,
              background: C.sunk,
              color: C.acc,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="cloudup" size={24} />
          </span>
        }
        title={word(t, `cmd.${rec.type}`, rec.type.replace(/_/g, ' '))}
        line={
          <span style={{ fontSize: 15, color: C.muted, display: 'flex', flexDirection: 'column' }}>
            {line && <span style={{ color: C.ink }}>{line}</span>}
            <span>
              {fmt.dateTime(rec.created_at)}
              {held > 0 && ` · ${t('sync.held', { count: held })}`}
            </span>
            {photos.length > 0 && (
              <span style={{ color: lost ? C.bad : sent < photos.length ? C.accDark : C.ok }}>
                {t('sync.d.photos', { sent, total: photos.length })}
                {lost > 0 && ` · ${t('sync.p.lostCount', { count: lost })}`}
              </span>
            )}
          </span>
        }
        right={
          <span
            style={{
              flexShrink: 0,
              padding: '2px 10px',
              borderRadius: 14,
              background: tone.bg,
              color: tone.fg,
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {word(t, `outbox.${rec.status}`, rec.status)}
          </span>
        }
      />
      {rec.status === 'SET_ASIDE' ? (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span style={{ flexGrow: 1, fontSize: 14, color: C.muted }}>
            {rec.set_aside &&
              t('sync.setAsideBy', {
                name: rec.set_aside.by_name ?? '—',
                when: fmt.relative(rec.set_aside.at),
              })}
            {reason && ` · ${reason}`}
          </span>
          {rec.set_aside?.root_id === rec.id && (
            <Btn icon="undo" onClick={() => void putBackCommand(rec.id)}>
              {t('sync.putBack')}
            </Btn>
          )}
        </div>
      ) : blocked ? (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span style={{ flexGrow: 1, fontSize: 14, color: C.muted }}>{t('sync.blocked')}</span>
          <Btn icon="supervisor" onClick={onSetAside}>
            {t('sync.setAside')}
          </Btn>
        </div>
      ) : (
        reason &&
        rec.status !== 'APPLIED' &&
        rec.status !== 'DUPLICATE' &&
        rec.status !== 'HELD' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 14, color: rec.status === 'ERROR' ? C.bad : C.muted }}>
              {reason}
              {(rec.status === 'ERROR' || rec.status === 'WAIT_FOR_PARENT') &&
                ` ${t('sync.officeSees')}`}
            </span>
            {rec.status === 'ERROR' && (
              <div
                style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Btn icon="undo" onClick={() => void retryCommand(rec.id)}>
                  {t('common.retry')}
                </Btn>
                <Btn icon="supervisor" onClick={onSetAside}>
                  {t('sync.setAside')}
                </Btn>
              </div>
            )}
          </div>
        )
      )}
    </div>
  )
}

/**
 * Photos still on the phone, each with what went wrong the last time it was sent, and a button
 * that sends them now (no waiting for the next round). So a stuck photo is never a mystery.
 */
function WaitingPhotos() {
  const { t } = useI18n()
  const fmt = useFormat()
  const photos = useLiveQuery(
    () =>
      getDb()
        .media.where('status')
        .anyOf(['PENDING', 'UPLOADING', 'ERROR', 'LOST'])
        .sortBy('created_at'),
    [],
    [],
  )
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const sendNow = async () => {
    setBusy(true)
    setResult(null)
    try {
      await retryLostMedia(photos.filter((p) => p.status === 'LOST').map((p) => p.id))
      const out = await uploadMedia(httpTransport.uploadMedia, {
        ids: photos.map((p) => p.id),
        force: true,
      })
      setResult(t('sync.photos.result', { sent: out.uploaded.length, failed: out.failed.length }))
    } finally {
      setBusy(false)
    }
  }

  if (photos.length === 0) return <Strip icon="ticks">{t('sync.p.allSent')}</Strip>
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        borderRadius: 12,
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
      }}
    >
      <b style={{ fontSize: 16 }}>{t('sync.photos.title')}</b>
      {photos.map((p) => (
        <div key={p.id} style={{ fontSize: 14, lineHeight: 1.35 }}>
          <div>
            {fmt.dateTime(new Date(p.created_at))} · {Math.round((p.size ?? 0) / 1024)} KB ·{' '}
            <b style={{ color: p.status === 'LOST' ? C.bad : C.accDark }}>
              {t(p.status === 'LOST' ? 'sync.p.LOST' : 'sync.p.WAITING')}
            </b>{' '}
            · {t('sync.photos.tries', { count: p.attempts })}
          </div>
          <div style={{ color: p.last_error ? C.bad : C.muted, wordBreak: 'break-word' }}>
            {p.last_error ?? t('sync.photos.noError')}
          </div>
        </div>
      ))}
      {result && <div style={{ fontSize: 14, fontWeight: 600 }}>{result}</div>}
      {busy ? (
        <WaitCta>{t('sync.photos.sending')}</WaitCta>
      ) : (
        <Cta label={t('sync.photos.sendNow')} icon="cloudup" onClick={() => void sendNow()} />
      )}
    </div>
  )
}
